/**
 * @file JS - Controller - SingleMapPageController.js
 * @author : Adam Lemire
 * @since 2019/06/13
 * @version 1.0
 * @summary Dernières modifications : 2023/03/27
 */

import 'isomorphic-fetch';
import Sortable from 'sortablejs';
import { unByKey } from 'ol/Observable';
import LayerControllerAdapter from '../../adapters/LayerControllerAdapter.js';
import SingleMapPage from '../../classes/Page/SingleMapPage.js'; // eslint-disable-line no-unused-vars
import RasterLayer from '../../classes/Layer/RasterLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayer from '../../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import { gisSettings, WebRasterLayer } from '../../gisSettings.js';
import { ContextMenu, LayerContextMenuOptions, LayerProperties, GisSection, ProjectMenu, GisButton, ImportDataForm, ImportResearcherDataForm } from '../../helpers/Selectors.js';
import { waitForElement } from '../../helpers/GlobalFunctions.js';
import { Evenements } from '../../events/Evenements.js';
import DataPageController from './DataPageController.js';
import DataPage from '../../classes/Page/DataPage.js'; // eslint-disable-line no-unused-vars
import LeftMenuController from '../LeftMenuController';
require('jquery-ui/ui/core');
require('jquery-ui/ui/effects/effect-shake');

/*
 * CONSTANTES
 */

const HTML_MENUGAUCHE_BTNS_MULTIMAP = '.left-menu__button--multimap';
const HTML_MENUGAUCHE_BTNS_SINGLEMAP = '.left-menu__button--singlemap';

const NOM_COUCHE_VECTEUR_PAR_DÉFAUT = 'Sous-districts de 1911';

const OPTION_LIBELLÉ = gisSettings.options.layer.coucheLabel.active;

/** SingleMapPageController sert à contrôler les objets SingleMapPage */
export default class SingleMapPageController {
  constructor () {
    /** @type { SingleMapPage } */
    this._pageCarteUnique = null;
    /** @type { LayerAdapter } */
    this._layerControllerAdapter = new LayerControllerAdapter();
    this._controleurPageTableau = new DataPageController();
  }

  get pageCarteUnique () { return this._pageCarteUnique; }

  set pageCarteUnique (pageCarteUnique) {
    console.log('SingleMapPageController: set pageCarteUnique');
    if (this.pageCarteUnique && this.pageCarteUnique.olMap) this._enleverSelectionFeatureSurCarteListener();
    $(GisSection.MAP_LAYERS_CONTAINER).empty();
    this._pageCarteUnique = pageCarteUnique;
    this._controleurPageTableau.dataPage = undefined;
    this._controleurPageTableau.dataPages = pageCarteUnique.attributeTables;
    this._restaurerOngletsPagesTableau();
    this._restoreMapLayersArray();
    this._écouterÉvénementsSpécifiques(pageCarteUnique);
    this._afficherPage(pageCarteUnique);
  }

  get dataPageController () { return this._controleurPageTableau; }

  /*
   * PUBLIC
   */

  activer () {
    this.activerEssentiel();
    this._ajouterCoucheParDéfaut();
  }

  activerNouvellePage () {
    // ! test sans registerGlobalListeners, donc vérifier si importer et changer source layer marche encore même si pas mis a jour listener des boutons
    this._ajouterCoucheParDéfaut();
  }

  activerEssentiel () {
    console.log('SingleMapPageController.activerEssentiel() -> ne devrait être appelé qu une fois');
    if (gisSettings.options.data.import.active) this._écouterBoutonsImporterDonnéesLocales();
    if (gisSettings.options.data.importerDepuisEspaceChercheur.active) this._inscrireAuditeursFormulaireImporterDonneesEspaceChercheur();
    this._écouterChangementVectorLayerSource();
    this._controleurPageTableau.activer();
  }

  /*
   * Global (valide peut-importe la singleMapPage active)
   */

  /** @param { number } idPageCarte */
  static ajouterSectionPageCarteUniqueDansVue (idPageCarte) {
    const newMapSection = '<div id="map__section-' + idPageCarte + '" class="map__section map__section-' +
      idPageCarte + '"><pre class="map__info map__info-' + idPageCarte + '"/></div>';
    $(GisSection.MAP).append(newMapSection);
  }

  /*
   * Spécifique (à une singleMapPage)
   */

  /**
   * @todo a voir quelles seront les couches chargées par défaut, et peut-être les mettre en paramètre pour tout pouvoir choisir du main.js
   */
  _ajouterCoucheParDéfaut () {
    this._pageCarteUnique.addRasterLayer(WebRasterLayer.OSM).then(v => { this._pageCarteUnique.addVectorLayer(NOM_COUCHE_VECTEUR_PAR_DÉFAUT); });
  }

  _écouterÉvénementsSpécifiques (pageCarteUnique) {
    this._écouterLayerAjoutéeAuModèle();
    this._écouterMenusContextuels();
    if (gisSettings.leftMenu.active && gisSettings.leftMenu.mapLayers.active) {
      this._activerTriageListeCouches();
    }
    this._écouterClicSurFeatureDeCoucheSélectionnée(pageCarteUnique);
    this._écouterFermerPageTableau();
    this._écouterChangementSélectionDepuisPageTableau();
  }

  async _restoreMapLayersArray () {
    console.log('_restoreMapLayersArray');
    
    const _this = this;
    const layers = await this._pageCarteUnique.layers;
    for (const layerZIndex of this._pageCarteUnique.layersZIndex.values()) {
      const layer = await layers.get(layerZIndex);
      _this._ajouterCoucheÀVueCarteListeCouches(await layer);
    }
  }

  _écouterLayerAjoutéeAuModèle () {
    const _this = this;
    this._pageCarteUnique.écouterLayerAjoutéeDansListe(async function (layer) {
      _this._ajouterCoucheÀVueCarteListeCouches(await layer);
    });
  }

  /** @param { VectorLayer | RasterLayer } layer */
  _ajouterCoucheÀVueCarteListeCouches (layer) {
    console.log('_ajouterCoucheÀVueCarteListeCouches : ' + layer.name);
    
    this._layerControllerAdapter.layer = layer;
    this._écouterChangementVisibilitéDeCouche();
    if (OPTION_LIBELLÉ) { this._écouterAffichageLayerLibelléModifié(); }
    this._écouterLayerSélectionnée();
    this._écouterChangementNomCouche();
    this._sélectionnerCouche(layer.id);
  }

  _écouterChangementVisibilitéDeCouche () {
    const _this = this;
    this._layerControllerAdapter.registerShowLayerListener(function (param) {
      _this._pageCarteUnique.olMap.renderSync();
      if (_this._layerControllerAdapter.layer.id === param.layerId && !param.visible) {
        cacherBulleInfoFeature(_this.pageCarteUnique.id);
      }
    });
  }

  _écouterAffichageLayerLibelléModifié () {
    const _this = this;
    this._layerControllerAdapter.écouterAffichageLibelléModifié(function (param) {
      _this._pageCarteUnique.olMap.renderSync();
    });
  }

  _écouterLayerSélectionnée () {
    const _this = this;
    this._layerControllerAdapter.écouterLayerSelectionnée(function (layerId) {
      _this._sélectionnerCouche(layerId);
    });
  }

  _écouterChangementNomCouche () {
    const _this = this;

    this._layerControllerAdapter.écouterNomLayerModifié(function (newName) {
      const layerNameDiv = '.map-layers__text-' + _this._layer.id;
      $(layerNameDiv).text(_this._layer.name);
    });
  }

  async _sélectionnerCouche (layerId) {
    const idMapLayerMenu = '#map-layers__layer-' + layerId;
    const menuMapLayerElement = await waitForElement(idMapLayerMenu);

    this.pageCarteUnique.selectLayerFromId(layerId);
    $('.map-layers__layer').removeClass('selected');
    $(menuMapLayerElement).addClass('selected');
  }

  _écouterChangementVectorLayerSource () {
    const _this = this;
    Evenements.vectorLayerSourceChangee.subscribe(_this._modifierVectorLayer.bind(_this));
  }

  /**
   * @param { object } objet
   * @param { VectorLayer } objet.vectorLayer
   * @param { VectorLayer.olLayer } objet.nouveauOlVectorLayer
   */
  _modifierVectorLayer (objet) {
    this._pageCarteUnique.modifierVectorLayer(objet);
    this._layerControllerAdapter.actualiserLégende();
    this._layerControllerAdapter.écouterCheckboxAfficherLayer(objet.vectorLayer);
  }

  _écouterMenusContextuels () {
    const _this = this;
    enleverÉcouteMenusContextuels();
    if (gisSettings.options.layer.editProperties.active) {
      $(LayerContextMenuOptions.PROPERTIES).on('click tap', () => _this._afficherPropriétésCouche($(ContextMenu.MAP_LAYERS).data('layerId')));
      $(LayerProperties.BUTTON.CANCEL).on('click tap', () => $(LayerProperties.SECTION).addClass('hidden'));
    }
    if (gisSettings.options.layer.remove.active) {
      $(LayerContextMenuOptions.REMOVE).off('click tap');
      $(LayerContextMenuOptions.REMOVE).on('click tap', () => _this._enleverCouche($(ContextMenu.MAP_LAYERS).data('layerId')));
    }
    if (gisSettings.options.layer.attributeTable.active) {
      $(LayerContextMenuOptions.ATTRIBUTES_TABLE).off('click tap');
      $(LayerContextMenuOptions.ATTRIBUTES_TABLE).on('click tap', () => _this._afficherPageTableau($(ContextMenu.MAP_LAYERS).data('layerId')));
    }
  }

  //! Utiliser Selectors.js pour id et class
  _activerTriageListeCouches () {
    const _this = this;
    try {
      const el = document.getElementById('map-layers__container');
      /* eslint-disable no-unused-vars */
      const sortable = new Sortable(el, {
        draggable: '.map-layers__layer',
        direction: 'vertical',
        onEnd: function (/** Event */ evt) {
          if (evt.oldIndex !== evt.newIndex) {
            console.log('index: ' + evt.newIndex);
            const children = $(GisSection.MAP_LAYERS_CONTAINER).children();
            const count = children.length;
            children.each(function (index, element) {
              const layerId = $(element).data('layerId');
              const newZIndex = count - index;
              console.log('layerId: ' + layerId);
              _this._pageCarteUnique.setLayerZIndex(layerId, newZIndex);
            });
          }
        }
      });
      /* eslint-enable no-unused-vars */
    } catch (err) {
      console.log(err);
    }
  }

  _enleverSelectionFeatureSurCarteListener () {
    const _this = this;
    this.pageCarteUnique.olMap.un('singleclick', _this._sélectionnerFeature.bind(_this));
  }

  /**
   * @param { SingleMapPage } pageCarteUnique
   */
  _afficherPage (pageCarteUnique) {
    console.log('afficherPage');
    const target = 'map__section-' + pageCarteUnique.id;

    console.log('target : ' + pageCarteUnique.target);
    if (pageCarteUnique.target !== target) pageCarteUnique.resetTarget();
    $(HTML_MENUGAUCHE_BTNS_MULTIMAP).hide();
    $(HTML_MENUGAUCHE_BTNS_SINGLEMAP).show();
    $('.top-menu__button--singlemap').show();
    $('.project-menu__option--singlemap').show();
    $(GisSection.DATA_TABS).show();
    LeftMenuController.afficherCouchesDeCarteActive();
  }

  /*
   * Page Tableau (DataPage) (table d'attributs)
   */

  /** @param { DataPage } dataPage */
  changerLangueDataPage (dataPage) {
    this._controleurPageTableau.recreerDataTable(dataPage);
  }

  _écouterFermerPageTableau () {
    const _this = this;
    this._controleurPageTableau.écouterSupprimer(function (dataPageId) {
      _this._pageCarteUnique.attributeTables.delete(dataPageId);
      _this._pageCarteUnique.refreshView();
    });
  }

  /** @param { number } vectorLayerId */
  async _afficherPageTableau (vectorLayerId) {
    this._pageCarteUnique && ajouterSectionTableauAVue(this._pageCarteUnique.id);
    console.log(this._pageCarteUnique.attributeTables);
    if (this._pageCarteUnique.attributeTables.has(vectorLayerId)) {
      console.log('_afficherPageTableauExistante');
      this._afficherPageTableauExistante(vectorLayerId);
    } else {
      console.log('_afficherPageTableau else -> nouvelle');
      this._afficherNouvellePageTableau(vectorLayerId);
    }
  }

  _restaurerOngletsPagesTableau () {
    const idRow = '#data-tabs-' + this._pageCarteUnique.id;

    $('.data-tabs__row').hide();
    $(idRow).show();
    $('.data-tab').removeClass('active');
  }

  // ! voir todo inclus
  _écouterChangementSélectionDepuisPageTableau () {
    const _this = this;
    this._controleurPageTableau.écouterSélectionChangée(function (objetSelection) {
      /** @type { VectorLayer } */
      const vectorLayer = objetSelection.vectorLayer;
      const idSelectionnes = objetSelection.idSelectionnes;

      vectorLayer.updateStyleFromSelectedFeatures(idSelectionnes);

      if (idSelectionnes.length === 0) {
        const infoDiv = '.map__info-' + _this.pageCarteUnique.id;
        $(infoDiv).html('');
        $(infoDiv).hide();
        _this.pageCarteUnique.selectedFeature = null;
      } else if (idSelectionnes.length === 1) {
        /* // @todo afficher propriété a partir de id de feature // ! a vérifier si encore le cas
        const properties = features[0].getProperties();

        $(infoDiv).html(JSON.stringify(properties, null, 2));
        $(infoDiv).show();
        _this.pageCarteUnique.selectedFeature = features[0]; */
      }
    });
  }

  /** @param { number } vectorLayerId */
  async _afficherNouvellePageTableau (vectorLayerId) {
    console.log('_afficherNouvellePageTableau');

    const dataPage = await this._créerNouvellePageTableau(vectorLayerId);
    const selection = this.pageCarteUnique.idSelectedFeatures;
    console.log(dataPage);

    Evenements.dataPageAjoutee.notify(await dataPage); // ! peut peut-être faire ici ce qui s'y fait dans ProjectController
    this._controleurPageTableau.afficherDataPage(await dataPage);
    if (selection.length > 0) {
      this._controleurPageTableau.modifierSelectionApresCreation(selection);
    }
  }

  /**
   * @param { number } vectorLayerId
   * @returns { DataPage }
   */
  async _créerNouvellePageTableau (vectorLayerId) {
    console.log('_créerNouvellePageTableau');
    const vectorLayer = await this._pageCarteUnique.getLayer(vectorLayerId);
    const dataPage = await this._controleurPageTableau.creerNouvelleDataPage(vectorLayer, this._pageCarteUnique.id);
    console.log(dataPage);

    this._pageCarteUnique.addDataPageToAttributeTables(dataPage);

    return dataPage;
  }

  /** @param { number } vectorLayerId */
  _afficherPageTableauExistante (vectorLayerId) {
    const dataPage = this._pageCarteUnique.attributeTables.get(vectorLayerId);
    this._controleurPageTableau.dataPage = dataPage;
    this._controleurPageTableau.afficherDataPage();
  }

  /*
   * PROPRIÉTÉS DE LA COUCHE/LAYER
   */

  /** @param {number} layerId */
  async _afficherPropriétésCouche (layerId) {
    const layer = await this._pageCarteUnique.getLayer(layerId);
    this._layerControllerAdapter.layer = await layer;
    this._layerControllerAdapter.updateLayerPropertiesView(await layer);
    $(LayerProperties.SECTION).removeClass('hidden');
  }

  /*
   * IMPORTER DONNÉES LOCALES - NON-FONCTIONNEL
   */

  _écouterBoutonsImporterDonnéesLocales () {
    if (gisSettings.topMenu.active && gisSettings.topMenu.projectMenu.active) {
      $(ProjectMenu.IMPORT_DATA_BUTTON).off('click tap').on('click tap', () => changerVisibilitéFormulaireImporterDonnées());
    }
    if (gisSettings.topMenu.active && gisSettings.topMenu.importDataButton.active) {
      $(GisButton.MAP_IMPORT_DATA).off('click tap').on('click tap', () => changerVisibilitéFormulaireImporterDonnées());
    }
    this._écouterBoutonsFormulaireImporterDonnéesLocales();
  }

  _écouterBoutonsFormulaireImporterDonnéesLocales () {
    const _this = this;
    $(ImportDataForm.FILE_INPUT).off('change').on('change', (event) => _this._écouterBoutonImporterFichierLocalSélectionné(event));
    $(ImportDataForm.CANCEL_BUTTON).off('click tap').on('click tap', () => changerVisibilitéFormulaireImporterDonnées());
    $(ImportDataForm.IMPORT_BUTTON).off('click tap').on('click tap', () => $(ImportDataForm.FILE_INPUT).effect('shake'));
  }

  /**
   * @description fichier provenant du client
   * @param { event } evt
   */
  _écouterBoutonImporterFichierLocalSélectionné (evt) {
    const _this = this;
    const file = evt.target.files[0]; // FileList object
    const paragrapheNomFichier = `<p class="file-preview__file-name">${file.name}</p>`;

    $(ImportDataForm.IMPORT_BUTTON).off('click tap');
    $(ImportDataForm.IMPORT_BUTTON).on('click tap', () => file !== 'undefined' ? _this._importerDonnéesLocales(file) : $(ImportDataForm.FILE_INPUT).effect('shake'));
    $(ImportDataForm.NO_FILE_TEXT).hide();
    $(ImportDataForm.FILE_NAME).remove();
    $(ImportDataForm.FILE_PREVIEW).append(paragrapheNomFichier);
  }

  /**
   * @todo ajouter url à la vectorLayer - non urgent, pas utilisé dans iBalsac
   * @param { string } jsonFile
   */
  _importerDonnéesLocales (jsonFile) {
    const _this = this;
    const reader = new window.FileReader();
    reader.onerror = gérerErreurOuvertureFichierLocal;
    reader.onabort = function (e) {
      console.log('File read cancelled');
    };
    reader.onloadend = function (evt) {
      if (evt.target.readyState === window.FileReader.DONE) {
        _this._importerDonnéesDepuisFichierJsonLocal(evt.target.result);
        // ! TODO ajouter url à la vectorLayer
        changerVisibilitéFormulaireImporterDonnées();
      }
    };
    reader.readAsText(jsonFile);
  }

  /** @param { string } jsonFile */
  async _importerDonnéesDepuisFichierJsonLocal (jsonFile) {
    const dataJSON = JSON.parse(jsonFile);
    dataJSON.api = false;
    this._pageCarteUnique.addVectorLayerFromData(dataJSON);
  }

  /*
   * IMPORTER DONNÉES DEPUIS ESPACE CHERCHEUR
   */

  _inscrireAuditeursFormulaireImporterDonneesEspaceChercheur () {
    if (gisSettings.topMenu.active && gisSettings.topMenu.projectMenu.active) {
      $(ProjectMenu.IMPORT_REASERCHER_DATA_BUTTON).off('click tap').on('click tap', () => chargerFormulaireImporterDonneesEspaceChercheur());
      $(GisButton.MAP_IMPORT_DATA_API).off('click tap').on('click tap', () => chargerFormulaireImporterDonneesEspaceChercheur());
    }
    this._inscrireAuditeursBoutonsFormulaireImporterDonneesEspaceChercheur();
  }

  _inscrireAuditeursBoutonsFormulaireImporterDonneesEspaceChercheur () {
    const _this = this;
    $(ImportResearcherDataForm.CANCEL_BUTTON).off('click tap').on('click tap', () => changerVisibilitéFormulaireImporterDonnéesEspaceChercheur());
    $(ImportResearcherDataForm.IMPORT_BUTTON).off('click tap').on('click tap', () => _this._gererImportationDonneesDepuisEspaceChercheur());
  }

  async _gererImportationDonneesDepuisEspaceChercheur () {
    const idFichier = $('.researcher-file-list__file input:checked').parent().data('id-fichier');

    if (idFichier) {
      const fichierJSON = await this._importerDonneesDepuisEspaceChercheur(idFichier);
      this._importerDonnéesDepuisAPI(await fichierJSON);
      changerVisibilitéFormulaireImporterDonnéesEspaceChercheur();
      console.log('idFichierDonneesEspaceChercheur = ' + idFichier);
    } else {
      $(ImportResearcherDataForm.IMPORT_BUTTON).effect('shake');
    }
  }

  /**
   * @param { number } idFichier
   * @returns { JSON }
   */
  async _importerDonneesDepuisEspaceChercheur (idFichier) {
    const urlapi = gisSettings.url.api.importerFichierDonneesEspaceChercheur + idFichier;
    const response = await fetch(urlapi, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
    const jsonData = await response.json();
    console.log('jsonData : %o', jsonData);
    console.log('jsonData.JSON_COUCHE : %o', jsonData.JSON_COUCHE);
    jsonData.JSON_COUCHE.api = true;
    jsonData.JSON_COUCHE.idFichierDonneesApi = jsonData.id;
    return jsonData.JSON_COUCHE;
  }

  async _importerDonnéesDepuisAPI (jsonFile) {
    const dataJSON = jsonFile;
    console.log('dataJSON : %o', dataJSON);
    this._pageCarteUnique.addVectorLayerFromData(dataJSON);
    Evenements.doitEnregistrer.notify(dataJSON);
  }

  /*
   * FEATURE INFO et Selected features
   */

  _écouterClicSurFeatureDeCoucheSélectionnée (pageCarteUnique) {
    console.log('_écouterClicSurFeatureDeCoucheSélectionnée');
    const _this = this;
    const olMap = pageCarteUnique.olMap;
    unByKey(this._eventKey);
    this._eventKey = olMap.on('singleclick', _this._sélectionnerFeature.bind(_this));
    return this._eventKey;
  }

  async _sélectionnerFeature (event) {
    if (!this.pageCarteUnique.selectedLayer.isVisible || this.pageCarteUnique.selectedLayer instanceof RasterLayer) return;
    const features = await this._obtenirFeaturesAuPixel(event.pixel);
    const featureSelectionnee = features[0];

    if (this.pageCarteUnique.idSelectedFeatures[0] && (features.length === 0 || (this.pageCarteUnique.idSelectedFeatures[0] === this.pageCarteUnique.getIdFromFeature(featureSelectionnee)))) {
      cacherBulleInfoFeature(this.pageCarteUnique.id);
      this.pageCarteUnique.updateSelectedLayerStyleFromSelectionChange();
      this._notiferSelectionChangéeDepuisCarte();
    } else if (featureSelectionnee) {
      this._afficherBulleInfoFeature(featureSelectionnee);
      this.pageCarteUnique.updateSelectedLayerStyleFromSelectionChange(featureSelectionnee);
      this._notiferSelectionChangéeDepuisCarte();
    }
  }

  async _obtenirFeaturesAuPixel (pixel) {
    const layerSelectionnee = await this.pageCarteUnique.selectedLayer.olLayer;
    const features = await layerSelectionnee.getFeatures(pixel);
    return features;
  }

  // ! à améliorer
  _afficherBulleInfoFeature (feature) {
    const infoDiv = '.map__info-' + this.pageCarteUnique.id;
    const proprietes = feature.getProperties();

    $(infoDiv).html(JSON.stringify(proprietes, null, 2));
    $(infoDiv).show();
  }

  async _notiferSelectionChangéeDepuisCarte () {
    const avecDonneesJointes = await this.pageCarteUnique.selectedLayer.joinField;
    if (!avecDonneesJointes) return;

    if (!this._pageCarteUnique.attributeTables.has(this.pageCarteUnique.selectedLayer.id)) {
      /* sert à créer la table d'attributs si elle n'existe pas, mais peut être dérangeant, donc enlevé pour l'instant
      const dataPage = await this._créerNouvellePageTableau(this.pageCarteUnique.selectedLayer.id);
      this._controleurPageTableau.modifierSelectionApresCreation(this.pageCarteUnique.idSelectedFeatures, dataPage);
      */
    } else {
      this._controleurPageTableau.dataPage = this._pageCarteUnique.attributeTables.get(this.pageCarteUnique.selectedLayer.id);
      this._controleurPageTableau.modifierSelection(this.pageCarteUnique.idSelectedFeatures);
    }
  }

  /*
   * CONTEXT-MENU
   */

  /** @param { number } layerId */
  _enleverCouche (layerId) {
    this._pageCarteUnique.removeLayer(layerId);
    const layerDiv = '#map-layers__layer-' + layerId;

    if (this._pageCarteUnique.attributeTables.has(layerId)) {
      this._controleurPageTableau.supprimer(this._pageCarteUnique.attributeTables.get(layerId));
    }

    $(layerDiv).remove();
    Evenements.doitEnregistrer.notify();
  }

  /*
   * JSON - Load
   */

  /** @param { SingleMapPage } pageCarteUnique */
  assignerPageCarteUniqueDepuisJSON (pageCarteUnique) {
    this._pageCarteUnique = pageCarteUnique;
    console.log('!!!!SingleMapPageController, assignerPageCarteUniqueDepuisJSON(pageCarteUnique) -> Ne doit être appelé qu\'une fois');
    this._écouterÉvénementsSpécifiques(pageCarteUnique);
    this._afficherPage(pageCarteUnique);
  }
}

/*
 * * FONCTIONS *
 */

/** */
async function créerListeHTMLFichiersEspaceChercheur () {
  const listeJSONFichiersEspaceChercheur = await obtenirListeFichiersEspaceChercheur('selections');

  if (listeJSONFichiersEspaceChercheur) {
    let listeHTML = '';
    if (listeJSONFichiersEspaceChercheur.length === 0) {
      listeHTML = '<div class="import-researcher-data-form__aucun-fichier" data-text="placeholder.noFile"></div>';
    } else {
      listeJSONFichiersEspaceChercheur.forEach(function (fichier) {
        const d = new Date(fichier.horodatage);

        listeHTML += '<label class="researcher-file-list__file" data-id-fichier="' + fichier.id + '">' + fichier.nomselection +
          '<input type="radio" name="researcher-file-radio">' +
          '<span class="checkmark"></span>' +
          '<span class="timestamp"> - ' + d.toISOString().split('.')[0].replace('T', ' ') + '</span>' +
          '</label>';
      });

      $(ImportResearcherDataForm.FILE_LIST).append(listeHTML);
    }
  } else {
    ajouterBoutonRéessayerChargerDonnéesEspaceChercheur();
  }
  Evenements.texteAjoute.notify();
}

/** */
function enleverÉcouteMenusContextuels () {
  $(LayerContextMenuOptions.PROPERTIES).off('click tap');
  $(LayerProperties.BUTTON.CANCEL).off('click tap');
  $(LayerContextMenuOptions.REMOVE).off('click tap');
}

/*
 * Tableau (DataPage)
 */

/** @param { number } singleMapPageId */
function ajouterSectionTableauAVue (singleMapPageId) {
  if (!document.getElementById('data__section-' + singleMapPageId)) {
    const newDataSection = '<div id="data__section-' + singleMapPageId + '" class="data__section data__section-' +
      singleMapPageId + '">';
    $(GisSection.DATA).append(newDataSection);
  }
}

/*
 * Formulaires
 */

/**
 * @param { string } typeFichier
 * @returns { JSON }
 */
async function obtenirListeFichiersEspaceChercheur (typeFichier) {
  try {
    const response = await fetch(gisSettings.url.api.listeFichiersDonneesEspaceChercheur + idChercheurActif, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
    const jsonR = await response.json();
    return jsonR;
  } catch (e) {
    console.log(e);
  }
}

/** */
function ajouterBoutonRéessayerChargerDonnéesEspaceChercheur () {
  $(ImportResearcherDataForm.FILE_LIST).empty();
  $(ImportResearcherDataForm.FILE_LIST).append('<button class="liste-donnees-chercheur__refresh-button" data-text="button.retry"></button>');
  $('.liste-donnees-chercheur__refresh-button').on('click tap', function () {
    $('.liste-donnees-chercheur__refresh-button').off('click tap');
    créerListeHTMLFichiersEspaceChercheur();
  });
}

/** */
function changerVisibilitéFormulaireImporterDonnées () {
  $(ImportDataForm.SECTION).toggleClass('hidden');
  $(ImportDataForm.FILE_NAME).remove();
  $(ImportDataForm.NO_FILE_TEXT).show();
  $(ImportDataForm.FILE_INPUT).val('');
}

/** */
function chargerFormulaireImporterDonneesEspaceChercheur () {
  changerVisibilitéFormulaireImporterDonnéesEspaceChercheur();
  créerListeHTMLFichiersEspaceChercheur();
}

/** */
function changerVisibilitéFormulaireImporterDonnéesEspaceChercheur () {
  $(ImportResearcherDataForm.SECTION).toggleClass('hidden');
  $(ImportResearcherDataForm.NO_FILE).show();
  $(ImportResearcherDataForm.FILE_LIST).empty();
}

/** @param { event } evt */
function gérerErreurOuvertureFichierLocal (evt) {
  switch (evt.target.error.code) {
    case evt.target.error.NOT_FOUND_ERR:
      window.alert('File Not Found!');
      break;
    case evt.target.error.NOT_READABLE_ERR:
      window.alert('File is not readable');
      break;
    case evt.target.error.ABORT_ERR:
      break;
    default:
      window.alert('An error occurred reading this file.');
  }
}

/*
 * Features et Sélection
 */

/** @param { number } singleMapPageId */
function cacherBulleInfoFeature (singleMapPageId) {
  const infoDiv = '.map__info-' + singleMapPageId;
  $(infoDiv).html('');
  $(infoDiv).hide();
}
